/**
 * Panel de propiedades del widget seleccionado.
 */
import React, { useEffect, useState } from 'react';
import { DATE_GRAIN_OPTIONS, WIDGET_TYPES, AGG_OPTIONS, INNER_AGG_OPTIONS, TOTAL_AGG_OPTIONS, CHART_PALETTE, NUMBER_FORMATS, TEXT_FONT_OPTIONS, TEXT_STYLE_DEFAULTS, getWidgetStyle, normalizeWidgetType, isDecorationType, isChartType, isContainerType, parseMetricSpec, describeMetricSpec, sourceLabel, widgetHasQueryShape } from './engineConfig';
import { applyFieldToWidget, removeFieldFromWidget, setMetricSpec, setMetricFormat, setMetricDecimals, setMetricTotalAgg, setMetricTotalFormula, setMetricConditionalFormats, setFieldAlias, updateFixedFilter, removeFixedFilter, preventDragOver, reorderDimensions, reorderVisibleMetrics, visibleMetricList, extractAggCalls, extractFormulaRefs, replaceNthAggFn, patchAggCalculatedField, CONDITION_OPS, createConditionalRule } from './fieldActions';
import { getAlias } from './filterUtils';
import FilterRuleCard from './FilterRuleCard';
import FormulaInput from './FormulaInput';
import { AGG_FN_FOR } from './formulaLanguage';
import ImageAssetField from './ImageAssetField';

const SHELF_MIME = 'application/x-shelf-reorder';

const WidgetConfigurator = ({
    widget,
    knownNames = [],
    draggingField,
    onChange,
    inputStyle,
    canvas,
    layoutItem,
    onLayoutItemChange,
    schemaColumns = [],
    dataSource,
    dataSources = [],
    onHide,
    heading = 'Propiedades',
    showHeading = true,
    onChangeSource
}) => {
    const [tab, setTab] = useState('data');
    const [dragOverKey, setDragOverKey] = useState(null);

    useEffect(() => {
        let raf = 0;
        let active = false;
        let x = 0;
        let y = 0;
        const step = () => {
            raf = 0;
            if (!active) return;
            document.querySelectorAll('.creator-panel-scroll').forEach((el) => {
                const rect = el.getBoundingClientRect();
                if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) return;
                const zone = 64;
                let dy = 0;
                if (y < rect.top + zone) dy = -Math.ceil(((rect.top + zone - y) / zone) * 24);
                else if (y > rect.bottom - zone) dy = Math.ceil(((y - (rect.bottom - zone)) / zone) * 24);
                if (dy) el.scrollTop += dy;
            });
            raf = requestAnimationFrame(step);
        };
        const onDragOver = (event) => {
            x = event.clientX;
            y = event.clientY;
            if (!active) {
                active = true;
                raf = requestAnimationFrame(step);
            }
        };
        const stop = () => {
            active = false;
            if (raf) cancelAnimationFrame(raf);
            raf = 0;
        };
        document.addEventListener('dragover', onDragOver, true);
        document.addEventListener('dragend', stop);
        document.addEventListener('drop', stop);
        return () => {
            stop();
            document.removeEventListener('dragover', onDragOver, true);
            document.removeEventListener('dragend', stop);
            document.removeEventListener('drop', stop);
        };
    }, []);

    if (!widget) {
        return (
            <div style={styles.empty}>
                Selecciona un elemento del lienzo para editarlo. Arriba están las propiedades del lienzo.
            </div>
        );
    }

    const query = widget.query || {};
    const dimensions = query.dimensions || [];
    const metrics = query.metrics || {};
    const visibleMetrics = visibleMetricList(query);
    const aggNames = (query.agg_calculated_fields || []).map(item => item.name);
    const rowCalcNames = (query.row_calculated_fields || []).map(item => item.name);
    const calcNames = [...aggNames, ...rowCalcNames];
    const formulaSuggestions = (() => {
        const seen = new Set();
        const items = [];
        const push = (name, kind) => {
            if (!name || seen.has(name)) return;
            seen.add(name);
            items.push({ name, kind });
        };
        dimensions.forEach(name => push(name, 'dimension'));
        visibleMetrics.forEach(name => push(name, calcNames.includes(name) ? 'calc' : 'metric'));
        schemaColumns.forEach(col => push(col.name, col.inferred_type === 'number' ? 'metric' : 'dimension'));
        calcNames.forEach(name => push(name, 'calc'));
        return items;
    })();
    const style = getWidgetStyle(widget);
    const type = normalizeWidgetType(widget.type);

    const patchWidget = (partial) => onChange({ ...widget, ...partial });
    const patchQuery = (partial) => onChange({ ...widget, query: { ...query, ...partial } });
    // Fechas y periodos año-mes («2026-03»): ambos sirven para agrupar y para el KPI.
    const isPeriodColumn = (col) => col?.inferred_type === 'date'
        || (Array.isArray(col?.values) && col.values.length > 0 && col.values.every(v => /^\d{4}-\d{1,2}$/.test(String(v))));
    const columnType = (name) => {
        const col = schemaColumns.find(item => item.name === name);
        return isPeriodColumn(col) ? 'date' : (col?.inferred_type || '');
    };
    const dateColumns = schemaColumns.filter(isPeriodColumn).map(col => col.name);
    const numberColumns = schemaColumns.filter(col => col.inferred_type === 'number').map(col => col.name);
    const kpiCfg = query.kpi || {};
    const patchKpi = (partial) => patchQuery({ kpi: { ...kpiCfg, ...partial } });
    const setDateGrain = (dim, grain) => {
        const next = { ...(query.dateGrains || {}) };
        if (grain) next[dim] = grain;
        else delete next[dim];
        patchQuery({ dateGrains: next });
    };
    const patchStyle = (partial) => patchWidget({
        style: { ...style, ...partial },
        color: partial.color !== undefined ? partial.color : (widget.color || style.color)
    });

    /** Campo de otra fuente sobre un elemento con datos: se ofrece cambiar su fuente. */
    const offerSourceChange = () => {
        const from = draggingField?.dataSourceId;
        if (!onChangeSource || !from || !query.dataSourceId || from === query.dataSourceId || !widgetHasQueryShape(query)) return false;
        onChangeSource(widget.id, from);
        return true;
    };

    const dropOn = (slot) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragOverKey(null);
        const moved = parseShelfDrag(event);
        if (moved) {
            if (moved.slot !== slot) return;
            const list = slot === 'dimension' ? dimensions : metricShelf;
            onChange(slot === 'dimension'
                ? reorderDimensions(widget, moved.index, list.length - 1)
                : reorderVisibleMetrics(widget, moved.index, list.length - 1));
            return;
        }
        if (!draggingField) return;
        if (offerSourceChange()) return;
        onChange(applyFieldToWidget(widget, draggingField, slot, knownNames));
    };

    const dropOnItem = (slot, toIndex) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragOverKey(null);
        const moved = parseShelfDrag(event);
        if (!moved) {
            if (!draggingField) return;
            if (offerSourceChange()) return;
            /* Un campo soltado sobre otro de la repisa entra en esa posición, no al final. */
            const next = applyFieldToWidget(widget, draggingField, slot, knownNames);
            const list = slot === 'dimension' ? (next.query?.dimensions || []) : visibleMetricList(next.query);
            const from = list.indexOf(draggingField.name);
            if (from < 0 || from === toIndex) {
                onChange(next);
                return;
            }
            onChange(slot === 'dimension' ? reorderDimensions(next, from, toIndex) : reorderVisibleMetrics(next, from, toIndex));
            return;
        }
        if (moved.slot !== slot || moved.index === toIndex) return;
        onChange(slot === 'dimension'
            ? reorderDimensions(widget, moved.index, toIndex)
            : reorderVisibleMetrics(widget, moved.index, toIndex));
    };

    const parseShelfDrag = (event) => {
        const raw = event.dataTransfer.getData(SHELF_MIME) || event.dataTransfer.getData('text/plain');
        if (!raw) return null;
        if (raw.startsWith('shelf:')) {
            const [, fromSlot, idx] = raw.split(':');
            return { slot: fromSlot, index: Number(idx) };
        }
        try {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.slot != null) return parsed;
        } catch (e) { /* ignore */ }
        return null;
    };

    const startShelfDrag = (slot, index) => (event) => {
        event.stopPropagation();
        event.dataTransfer.setData(SHELF_MIME, JSON.stringify({ slot, index }));
        event.dataTransfer.setData('text/plain', `shelf:${slot}:${index}`);
        event.dataTransfer.effectAllowed = 'move';
    };

    const metricShelf = visibleMetrics;

    return (
        <div style={styles.panel}>
            {showHeading && (
            <div style={styles.titleRow}>
                <h3 style={styles.title}>{heading}</h3>
                {onHide && (
                    <button type="button" style={styles.hideBtn} onClick={onHide} title="Ocultar propiedades">›</button>
                )}
            </div>
            )}
            <div style={styles.tabs}>
                <button type="button" style={tab === 'data' ? styles.tabOn : styles.tab} onClick={() => setTab('data')}>Datos</button>
                <button type="button" style={tab === 'style' ? styles.tabOn : styles.tab} onClick={() => setTab('style')}>Estilo</button>
            </div>

            {tab === 'data' && (
                <>
                    {dataSources.length > 0 && !isDecorationType(type) && (
                        <>
                            <label style={styles.label}>Fuente de datos</label>
                            <select
                                value={query.dataSourceId || ''}
                                onChange={e => {
                                    const next = dataSources.find(item => item.id === e.target.value);
                                    if (!next) return;
                                    /* Con datos, el cambio pasa por la ventana que empareja columnas. */
                                    if (onChangeSource && widgetHasQueryShape(query)) {
                                        onChangeSource(widget.id, next.id);
                                        return;
                                    }
                                    patchWidget({
                                        query: {
                                            ...query,
                                            dataSourceId: next.id,
                                            spreadsheet_id: next.spreadsheet_id || '',
                                            sheet_name: next.sheet_name || '',
                                            dimensions: [],
                                            metrics: {},
                                            visibleMetrics: [],
                                            row_calculated_fields: [],
                                            agg_calculated_fields: [],
                                            fixedFilters: []
                                        }
                                    });
                                }}
                                style={inputStyle}
                            >
                                <option value="">Selecciona una fuente</option>
                                {dataSources.map(item => (
                                    <option key={item.id} value={item.id}>{sourceLabel(item)}</option>
                                ))}
                            </select>
                        </>
                    )}

                    <label style={styles.label}>Título</label>
                    <input
                        type="text"
                        value={widget.title || ''}
                        onChange={e => patchWidget({ title: e.target.value })}
                        style={inputStyle}
                    />

                    <label style={styles.label}>Visualización</label>
                    <select
                        value={widget.type}
                        onChange={e => {
                            const nextType = e.target.value;
                            const patch = { type: nextType };
                            if (nextType === 'container' && !widget.children) {
                                patch.children = { widgets: [], layout: [] };
                                patch.style = {
                                    ...style,
                                    innerPadding: style.innerPadding ?? 0,
                                    innerGap: style.innerGap ?? 0,
                                    showChildHeaders: true,
                                    titleFontSize: style.titleFontSize || 16,
                                    titleColor: style.titleColor || '#4a148c',
                                    titleAlign: style.titleAlign || 'left',
                                    cardPadding: style.cardPadding ?? 0,
                                    cardMargin: style.cardMargin ?? 0
                                };
                            }
                            if (nextType === 'text') {
                                patch.content = widget.content || 'Escribe el texto del dashboard…';
                                patch.style = { ...TEXT_STYLE_DEFAULTS, ...style, color: style.color === CHART_PALETTE[0] ? '#202124' : (style.color || '#202124'), showTitle: false };
                                patch.color = '#202124';
                            }
                            if (nextType === 'image') {
                                patch.style = {
                                    ...style,
                                    showTitle: false,
                                    objectFit: style.objectFit || 'contain',
                                    cardBackground: style.cardBackground || 'transparent',
                                    cardPadding: 0
                                };
                            }
                            patchWidget(patch);
                        }}
                        style={inputStyle}
                    >
                        {WIDGET_TYPES.map(item => (
                            <option key={item.type} value={item.type}>{item.label}</option>
                        ))}
                    </select>

                    {layoutItem && onLayoutItemChange && (
                        <div style={styles.spanRow}>
                            <label style={styles.spanBox}>
                                Ancho
                                <input
                                    type="number"
                                    min={1}
                                    max={canvas?.columns || 8}
                                    value={layoutItem.w}
                                    onChange={e => onLayoutItemChange({ w: Math.max(1, Math.min(canvas?.columns || 8, Number(e.target.value) || 1)) })}
                                    style={styles.spanInput}
                                />
                                <span style={styles.hint}>/ {canvas?.columns || 8} bloques</span>
                            </label>
                            <label style={styles.spanBox}>
                                Alto
                                <input
                                    type="number"
                                    min={1}
                                    max={40}
                                    value={layoutItem.h}
                                    onChange={e => onLayoutItemChange({ h: Math.max(1, Number(e.target.value) || 1) })}
                                    style={styles.spanInput}
                                />
                            </label>
                        </div>
                    )}

                    {type === 'image' && (
                        <>
                            <p style={styles.hint}>La imagen es un elemento del lienzo. No usa fuentes de datos.</p>
                            <ImageAssetField
                                label="Archivo o URL"
                                value={widget.content || ''}
                                onChange={(url) => patchWidget({ content: url })}
                            />
                        </>
                    )}

                    {type === 'text' && (
                        <>
                            <label style={styles.label}>Contenido</label>
                            <textarea
                                value={widget.content || ''}
                                onChange={e => patchWidget({ content: e.target.value })}
                                style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                            />
                        </>
                    )}

                    {type === 'divider' && (
                        <p style={styles.hint}>Usa Estilo para color, grosor y orientación de la línea.</p>
                    )}

                    {type === 'container' && (
                        <>
                            <p style={styles.hint}>
                                Este bloque es un sublienzo. Selecciónalo y pulsa + Texto, + Barras o + Tabla para colocar elementos dentro. Título, colores y márgenes se ajustan en Estilo.
                            </p>
                            <label style={styles.label}>Subtítulo del bloque</label>
                            <input
                                type="text"
                                value={style.subtitle || ''}
                                onChange={e => patchStyle({ subtitle: e.target.value })}
                                style={inputStyle}
                                placeholder="Texto secundario bajo el título"
                            />
                        </>
                    )}

                    {type === 'slicer' ? (
                        <>
                            <h4 style={styles.section}>Campo del filtro</h4>
                            <p style={styles.hint}>Arrastra una dimensión. Este control filtra todo el informe, igual que la barra de filtros.</p>
                            <div
                                style={{ ...styles.shelf, outline: draggingField ? '2px dashed #174ea6' : '1px dashed #dadce0' }}
                                onDragOver={preventDragOver}
                                onDrop={dropOn('dimension')}
                            >
                                {dimensions.length === 0 && <span style={styles.shelfHint}>Suelta el campo aquí</span>}
                                {dimensions.map((name, index) => (
                                    <FieldPill
                                        key={name}
                                        name={name}
                                        alias={getAlias(query, name)}
                                        tone="dim"
                                        highlight={dragOverKey === `dimension-${index}`}
                                        onAlias={value => onChange(setFieldAlias(widget, name, value))}
                                        onRemove={() => onChange(removeFieldFromWidget(widget, name, 'dimension'))}
                                        onDragStart={startShelfDrag('dimension', index)}
                                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverKey(`dimension-${index}`); }}
                                        onDragLeave={() => setDragOverKey(null)}
                                        onDrop={dropOnItem('dimension', index)}
                                    />
                                ))}
                            </div>
                        </>
                    ) : !isDecorationType(type) && !isContainerType(type) ? (
                        <>
                            {type !== 'kpi' && (
                                <>
                                    <h4 style={styles.section}>Dimensiones</h4>
                                    <p style={styles.hint}>Arrastra las pastillas hacia arriba o abajo para cambiar el orden. El alias no altera el campo original.</p>
                                    <div
                                        style={{ ...styles.shelf, outline: draggingField ? '2px dashed #174ea6' : '1px dashed #dadce0' }}
                                        onDragOver={preventDragOver}
                                        onDrop={dropOn('dimension')}
                                    >
                                        {dimensions.length === 0 && <span style={styles.shelfHint}>Suelta una dimensión aquí</span>}
                                        {dimensions.map((name, index) => (
                                            <FieldPill
                                                key={name}
                                                name={name}
                                                alias={getAlias(query, name)}
                                                tone="dim"
                                                highlight={dragOverKey === `dimension-${index}`}
                                                onAlias={value => onChange(setFieldAlias(widget, name, value))}
                                                onRemove={() => onChange(removeFieldFromWidget(widget, name, 'dimension'))}
                                                onDragStart={startShelfDrag('dimension', index)}
                                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverKey(`dimension-${index}`); }}
                                                onDragLeave={() => setDragOverKey(null)}
                                                onDrop={dropOnItem('dimension', index)}
                                            />
                                        ))}
                                    </div>
                                    {dimensions.filter(name => columnType(name) === 'date').map(name => (
                                        <label key={`grain-${name}`} style={styles.grainRow}>
                                            <span style={styles.grainLabel}>Agrupar «{getAlias(query, name)}» por</span>
                                            <select
                                                value={query.dateGrains?.[name] || ''}
                                                onChange={e => setDateGrain(name, e.target.value)}
                                                style={inputStyle}
                                            >
                                                {DATE_GRAIN_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </label>
                                    ))}
                                </>
                            )}

                            <h4 style={styles.section}>Métricas</h4>
                            <p style={styles.hint}>Pulsa ▸ para abrir las opciones de cada métrica. Arrastra ⋮⋮ para reordenar.</p>
                            <div
                                style={{ ...styles.shelf, outline: draggingField ? '2px dashed #137333' : '1px dashed #dadce0' }}
                                onDragOver={preventDragOver}
                                onDrop={dropOn('metric')}
                            >
                                {metricShelf.length === 0 && <span style={styles.shelfHint}>Suelta una métrica o campo calculado aquí</span>}
                                {metricShelf.map((name, index) => {
                                    const rawFmt = query.metricFormats?.[name];
                                    const formatType = !rawFmt ? 'inherit' : (typeof rawFmt === 'object' ? (rawFmt.type || 'inherit') : rawFmt);
                                    const decimals = typeof rawFmt === 'object' && rawFmt.decimals != null ? rawFmt.decimals : '';
                                    return (
                                    <FieldPill
                                        key={name}
                                        name={name}
                                        alias={getAlias(query, name)}
                                        tone="met"
                                        aggSpec={parseMetricSpec(metrics[name])}
                                        innerFields={schemaColumns.map(col => col.name).filter(col => col !== name)}
                                        isRatio={aggNames.includes(name)}
                                        formula={(query.agg_calculated_fields || []).find(item => item.name === name)?.formula
                                            || (query.row_calculated_fields || []).find(item => item.name === name)?.formula}
                                        totalAgg={query.totalAggs?.[name] || (calcNames.includes(name) ? 'formula' : 'auto')}
                                        totalFormula={query.totalFormulas?.[name] || ''}
                                        formulaSuggestions={formulaSuggestions}
                                        knownNames={knownNames}
                                        refMetrics={metrics}
                                        onTotalAgg={value => onChange(setMetricTotalAgg(widget, name, value))}
                                        onTotalFormula={value => onChange(setMetricTotalFormula(widget, name, value))}
                                        onFormulaChange={next => onChange(patchAggCalculatedField(widget, name, { formula: next }))}
                                        onRefAgg={(ref, agg) => onChange(setMetricSpec(widget, ref, { agg }))}
                                        highlight={dragOverKey === `metric-${index}`}
                                        onAggSpec={patch => onChange(setMetricSpec(widget, name, patch))}
                                        format={formatType}
                                        decimals={decimals}
                                        onFormat={value => onChange(setMetricFormat(widget, name, value))}
                                        onDecimals={value => onChange(setMetricDecimals(widget, name, value))}
                                        conditionalFormats={query.conditionalFormats?.[name] || []}
                                        onConditionalFormats={rules => onChange(setMetricConditionalFormats(widget, name, rules))}
                                        onAlias={value => onChange(setFieldAlias(widget, name, value))}
                                        onRemove={() => onChange(removeFieldFromWidget(widget, name, 'metric'))}
                                        onDragStart={startShelfDrag('metric', index)}
                                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverKey(`metric-${index}`); }}
                                        onDragLeave={() => setDragOverKey(null)}
                                        onDrop={dropOnItem('metric', index)}
                                    />
                                    );
                                })}
                            </div>
                        </>
                    ) : null}

                    {!isDecorationType(type) && type !== 'slicer' && !isContainerType(type) && (
                        <>
                            <h4 style={styles.section}>Filtro de este gráfico</h4>
                            <p style={styles.hint}>Solo afecta a este elemento. Arrastra un campo; la barra de arriba filtra todo el informe.</p>
                            <div
                                style={{ ...styles.shelf, outline: draggingField ? '2px dashed #e65100' : '1px dashed #dadce0' }}
                                onDragOver={preventDragOver}
                                onDrop={dropOn('filter')}
                            >
                                {(query.fixedFilters || []).length === 0 && <span style={styles.shelfHint}>Suelta un campo para fijar un valor</span>}
                                {(query.fixedFilters || []).map(item => (
                                    <FilterRuleCard
                                        key={item.id}
                                        item={item}
                                        columns={schemaColumns}
                                        dataSource={dataSource}
                                        onChange={(partial) => onChange(updateFixedFilter(widget, item.id, partial))}
                                        onRemove={() => onChange(removeFixedFilter(widget, item.id))}
                                    />
                                ))}
                            </div>
                        </>
                    )}

                    {type === 'kpi' && (
                        <>
                            <h4 style={styles.section}>Comparación, meta y tendencia</h4>
                            <label style={styles.label}>Campo de fecha</label>
                            <select value={kpiCfg.dateField || ''} onChange={e => patchKpi({ dateField: e.target.value })} style={inputStyle}>
                                <option value="">{dateColumns.length ? 'Elige una fecha…' : 'La fuente no tiene fechas'}</option>
                                {dateColumns.map(name => <option key={name} value={name}>{name}</option>)}
                            </select>
                            <label style={styles.label}>Comparar con</label>
                            <select
                                value={kpiCfg.compare || ''}
                                onChange={e => patchKpi({ compare: e.target.value })}
                                style={inputStyle}
                                disabled={!kpiCfg.dateField}
                            >
                                <option value="">Sin comparación</option>
                                <option value="previous">Periodo anterior</option>
                                <option value="previous_year">Mismo periodo del año anterior</option>
                            </select>
                            {kpiCfg.compare && (
                                <>
                                    <label style={styles.label}>Sin filtro de fechas, comparar el último</label>
                                    <select value={kpiCfg.compareGrain || 'month'} onChange={e => patchKpi({ compareGrain: e.target.value })} style={inputStyle}>
                                        {DATE_GRAIN_OPTIONS.filter(opt => opt.value).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                    </select>
                                    <p style={styles.hint}>Con un filtro de fechas activo se compara contra el rango anterior de la misma duración.</p>
                                    <label style={styles.label}>Mejor si el valor</label>
                                    <select value={kpiCfg.better || 'up'} onChange={e => patchKpi({ better: e.target.value })} style={inputStyle}>
                                        <option value="up">Sube (ventas, alcance…)</option>
                                        <option value="down">Baja (costos, CPC…)</option>
                                    </select>
                                </>
                            )}
                            <label style={styles.label}>Meta</label>
                            <select value={kpiCfg.targetMode || ''} onChange={e => patchKpi({ targetMode: e.target.value })} style={inputStyle}>
                                <option value="">Sin meta</option>
                                <option value="value">Valor fijo</option>
                                <option value="column">Columna de la fuente (suma)</option>
                            </select>
                            {kpiCfg.targetMode === 'value' && (
                                <input
                                    type="number"
                                    value={kpiCfg.targetValue ?? ''}
                                    onChange={e => patchKpi({ targetValue: e.target.value })}
                                    style={inputStyle}
                                    placeholder="Ej. 1000000"
                                />
                            )}
                            {kpiCfg.targetMode === 'column' && (
                                <select value={kpiCfg.targetColumn || ''} onChange={e => patchKpi({ targetColumn: e.target.value })} style={inputStyle}>
                                    <option value="">Elige la columna de meta…</option>
                                    {numberColumns.map(name => <option key={name} value={name}>{name}</option>)}
                                </select>
                            )}
                            <label style={styles.label}>Tendencia (sparkline)</label>
                            <select
                                value={kpiCfg.sparkGrain || ''}
                                onChange={e => patchKpi({ sparkGrain: e.target.value })}
                                style={inputStyle}
                                disabled={!kpiCfg.dateField}
                            >
                                <option value="">Sin tendencia</option>
                                {DATE_GRAIN_OPTIONS.filter(opt => opt.value).map(opt => <option key={opt.value} value={opt.value}>Por {opt.label.toLowerCase()}</option>)}
                            </select>
                        </>
                    )}
                </>
            )}

            {tab === 'style' && (
                <div style={styles.styleGrid}>
                    {type === 'image' && (
                        <>
                            <label style={styles.label}>Ajuste</label>
                            <select
                                value={style.objectFit || 'contain'}
                                onChange={e => patchStyle({ objectFit: e.target.value })}
                                style={inputStyle}
                            >
                                <option value="contain">Contener</option>
                                <option value="cover">Cubrir</option>
                                <option value="fill">Estirar</option>
                            </select>
                        </>
                    )}

                    <Toggle label="Mostrar título" checked={style.showTitle !== false} onChange={v => patchStyle({ showTitle: v })} />
                    <Toggle label="Botón de exportar" checked={style.showExport !== false} onChange={v => patchStyle({ showExport: v })} />
                    {!['text', 'container', 'divider', 'slicer', 'image'].includes(type) && (
                        <Toggle
                            label={type === 'table' ? 'Mostrar encabezado de columnas' : 'Mostrar encabezado'}
                            checked={style.showHeader !== false}
                            onChange={v => patchStyle({ showHeader: v })}
                        />
                    )}
                    <label style={styles.label}>Fondo del bloque</label>
                    <input type="color" value={style.cardBackground || '#ffffff'} onChange={e => patchStyle({ cardBackground: e.target.value })} style={styles.colorInput} />
                    <label style={styles.label}>Esquinas ({style.cardRadius ?? 10}px)</label>
                    <input type="range" min="0" max="24" value={style.cardRadius ?? 10} onChange={e => patchStyle({ cardRadius: Number(e.target.value) })} />

                    <label style={styles.label}>{type === 'text' ? 'Color del texto' : type === 'container' ? 'Color de acento' : 'Color principal'}</label>
                    <div style={styles.swatches}>
                        {CHART_PALETTE.map(hex => (
                            <button
                                key={hex}
                                type="button"
                                onClick={() => patchStyle({ color: hex })}
                                style={{
                                    ...styles.swatch,
                                    backgroundColor: hex,
                                    outline: style.color === hex ? '2px solid #202124' : '2px solid transparent'
                                }}
                            />
                        ))}
                        <input type="color" value={style.color || '#202124'} onChange={e => patchStyle({ color: e.target.value })} style={styles.colorInput} />
                    </div>

                    {type === 'container' && (
                        <>
                            <Toggle
                                label="Barra de cada elemento interno"
                                checked={style.showChildHeaders !== false}
                                onChange={v => patchStyle({ showChildHeaders: v })}
                            />
                            <h4 style={styles.section}>Título del bloque</h4>
                            <label style={styles.label}>Texto del título</label>
                            <input
                                type="text"
                                value={widget.title || ''}
                                onChange={e => patchWidget({ title: e.target.value })}
                                style={inputStyle}
                                placeholder="Título del bloque"
                            />
                            <label style={styles.label}>Fuente</label>
                            <select value={style.titleFontFamily || 'inherit'} onChange={e => patchStyle({ titleFontFamily: e.target.value })} style={inputStyle}>
                                {TEXT_FONT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <label style={styles.label}>Tamaño ({style.titleFontSize || 16}px)</label>
                            <input type="range" min="12" max="36" value={style.titleFontSize || 16} onChange={e => patchStyle({ titleFontSize: Number(e.target.value) })} />
                            <label style={styles.label}>Peso</label>
                            <select value={style.titleFontWeight || 700} onChange={e => patchStyle({ titleFontWeight: Number(e.target.value) })} style={inputStyle}>
                                <option value={400}>Regular</option>
                                <option value={600}>Seminegrita</option>
                                <option value={700}>Negrita</option>
                            </select>
                            <Toggle label="Cursiva" checked={!!style.titleItalic} onChange={v => patchStyle({ titleItalic: v })} />
                            <label style={styles.label}>Alineación</label>
                            <select value={style.titleAlign || 'left'} onChange={e => patchStyle({ titleAlign: e.target.value })} style={inputStyle}>
                                <option value="left">Izquierda</option>
                                <option value="center">Centro</option>
                                <option value="right">Derecha</option>
                            </select>
                            <label style={styles.label}>Color del título</label>
                            <input type="color" value={style.titleColor || '#4a148c'} onChange={e => patchStyle({ titleColor: e.target.value })} style={styles.colorInput} />
                            <label style={styles.label}>Subtítulo</label>
                            <input
                                type="text"
                                value={style.subtitle || ''}
                                onChange={e => patchStyle({ subtitle: e.target.value })}
                                style={inputStyle}
                                placeholder="Opcional"
                            />
                            {!!style.subtitle && (
                                <>
                                    <label style={styles.label}>Tamaño del subtítulo ({style.subtitleFontSize || 12}px)</label>
                                    <input type="range" min="10" max="20" value={style.subtitleFontSize || 12} onChange={e => patchStyle({ subtitleFontSize: Number(e.target.value) })} />
                                    <label style={styles.label}>Color del subtítulo</label>
                                    <input type="color" value={style.subtitleColor || '#5f6368'} onChange={e => patchStyle({ subtitleColor: e.target.value })} style={styles.colorInput} />
                                </>
                            )}
                            <h4 style={styles.section}>Caja y espacios</h4>
                            <label style={styles.label}>Padding interior (alrededor de los elementos) ({style.innerPadding ?? 0}px)</label>
                            <input
                                type="range"
                                min="0"
                                max="48"
                                value={style.innerPadding ?? 0}
                                onChange={e => patchStyle({ innerPadding: Number(e.target.value) })}
                            />
                            <label style={styles.label}>Espacio entre elementos ({style.innerGap ?? 0}px)</label>
                            <input
                                type="range"
                                min="0"
                                max="32"
                                value={style.innerGap ?? 0}
                                onChange={e => patchStyle({ innerGap: Number(e.target.value) })}
                            />
                            <label style={styles.label}>Padding de la caja (título y borde) ({style.cardPadding ?? 0}px)</label>
                            <input
                                type="range"
                                min="0"
                                max="48"
                                value={style.cardPadding ?? 0}
                                onChange={e => patchStyle({ cardPadding: Number(e.target.value) })}
                            />
                            <label style={styles.label}>Margen exterior ({style.cardMargin ?? 0}px)</label>
                            <input
                                type="range"
                                min="0"
                                max="48"
                                value={style.cardMargin ?? 0}
                                onChange={e => patchStyle({ cardMargin: Number(e.target.value) })}
                            />
                            <label style={styles.label}>Borde ({style.borderWidth || 0}px)</label>
                            <input
                                type="range"
                                min="0"
                                max="8"
                                value={style.borderWidth || 0}
                                onChange={e => patchStyle({ borderWidth: Number(e.target.value) })}
                            />
                            {(style.borderWidth || 0) > 0 && (
                                <>
                                    <label style={styles.label}>Color del borde</label>
                                    <input type="color" value={style.borderColor || '#7e57c2'} onChange={e => patchStyle({ borderColor: e.target.value })} style={styles.colorInput} />
                                </>
                            )}
                            <p style={styles.hint}>Todo puede ir a 0. El padding interior rodea los elementos; el de la caja rodea título y contenido; el margen aleja el bloque de su celda.</p>
                        </>
                    )}

                    {!['text', 'container', 'divider', 'slicer', 'image'].includes(type) && (
                        <>
                            <label style={styles.label}>Formato numérico por defecto</label>
                            <select value={style.numberFormat} onChange={e => patchStyle({ numberFormat: e.target.value })} style={inputStyle}>
                                {NUMBER_FORMATS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <p style={styles.hint}>Cada métrica puede overridear este formato en la pestaña Datos.</p>
                            <label style={styles.label}>Decimales por defecto</label>
                            <input
                                type="number"
                                min="0"
                                max="8"
                                placeholder="Auto"
                                value={style.numberDecimals ?? ''}
                                onChange={e => patchStyle({ numberDecimals: e.target.value === '' ? null : Number(e.target.value) })}
                                style={inputStyle}
                            />
                            <p style={styles.hint}>Vacío = automático según el formato. Cada métrica puede overridearlo.</p>
                        </>
                    )}

                    {type === 'table' && (
                        <>
                            <Toggle label="Filas alternadas" checked={style.striped} onChange={v => patchStyle({ striped: v })} />
                            <Toggle label="Fila de totales" checked={style.showTotals} onChange={v => patchStyle({ showTotals: v })} />
                            <Toggle label="Fijar primera columna" checked={style.freezeFirstColumn} onChange={v => patchStyle({ freezeFirstColumn: v })} />
                            <Toggle
                                label="Combinar celdas repetidas"
                                checked={style.mergeRepeatedCells !== false}
                                onChange={v => patchStyle({ mergeRepeatedCells: v })}
                            />
                            <p style={styles.hint}>Como Tableau: valores iguales consecutivos en dimensiones se agrupan en una sola celda.</p>
                            <Toggle
                                label="Ajustar texto de encabezados"
                                checked={style.headerWrap !== false}
                                onChange={v => patchStyle({ headerWrap: v })}
                            />
                            <p style={styles.hint}>Si no cabe, el título baja de línea. En el alias de cada campo puedes pulsar Enter para un salto manual.</p>
                            <label style={styles.label}>Ancho de la tabla</label>
                            <select value={style.tableWidthMode === 'scroll' ? 'scroll' : 'fit'} onChange={e => patchStyle({ tableWidthMode: e.target.value })} style={inputStyle}>
                                <option value="fit">Ajustar al ancho del bloque</option>
                                <option value="scroll">Columnas a su ancho (con desplazamiento)</option>
                            </select>
                            <p style={styles.hint}>Ajustada, las columnas se reparten el ancho sin barra horizontal; el texto que no cabe termina en «…» y se lee completo al pasar el mouse.</p>
                            <Toggle
                                label="Paginar resultados"
                                checked={style.tablePaginate !== false}
                                onChange={v => patchStyle({ tablePaginate: v })}
                            />
                            <label style={styles.label}>Top N (0 = todos)</label>
                            <input
                                type="number"
                                min="0"
                                max="500"
                                value={style.topN || 0}
                                onChange={e => patchStyle({ topN: Math.max(0, Number(e.target.value) || 0) })}
                                style={inputStyle}
                            />
                            {Number(style.topN) > 0 && dimensions.length === 1 && (
                                <Toggle
                                    label="Agrupar el resto en «Otros»"
                                    checked={style.topNOthers}
                                    onChange={v => patchStyle({ topNOthers: v })}
                                />
                            )}
                            <p style={styles.hint}>Después de ordenar, la tabla solo muestra las primeras N filas. Los totales usan esas mismas filas.</p>
                            <label style={styles.label}>Ordenar por</label>
                            <select value={style.sortKey || ''} onChange={e => patchStyle({ sortKey: e.target.value })} style={inputStyle}>
                                <option value="">Sin orden fijo</option>
                                {dimensions.map(name => (
                                    <option key={`d-${name}`} value={name}>{getAlias(query, name)}</option>
                                ))}
                                {metricShelf.map(name => (
                                    <option key={`m-${name}`} value={name}>{getAlias(query, name)}</option>
                                ))}
                            </select>
                            <select value={style.sortDir || 'desc'} onChange={e => patchStyle({ sortDir: e.target.value })} style={inputStyle}>
                                <option value="desc">Mayor a menor / Z–A</option>
                                <option value="asc">Menor a mayor / A–Z</option>
                            </select>
                            <p style={styles.hint}>También puedes pulsar un encabezado para cambiar el orden al vuelo.</p>
                            <label style={styles.label}>Densidad</label>
                            <select value={style.tableDensity} onChange={e => patchStyle({ tableDensity: e.target.value })} style={inputStyle}>
                                <option value="comfortable">Cómoda</option>
                                <option value="compact">Compacta</option>
                            </select>
                            <label style={styles.label}>Fuente de la tabla</label>
                            <select value={style.tableFontFamily || 'inherit'} onChange={e => patchStyle({ tableFontFamily: e.target.value })} style={inputStyle}>
                                {TEXT_FONT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>

                            <h4 style={styles.section}>Encabezado</h4>
                            <label style={styles.label}>Tamaño ({style.headerFontSize || 13}px)</label>
                            <input type="range" min="10" max="22" value={style.headerFontSize || 13} onChange={e => patchStyle({ headerFontSize: Number(e.target.value) })} />
                            <label style={styles.label}>Peso</label>
                            <select value={style.headerFontWeight || 600} onChange={e => patchStyle({ headerFontWeight: Number(e.target.value) })} style={inputStyle}>
                                <option value={400}>Regular</option>
                                <option value={600}>Seminegrita</option>
                                <option value={700}>Negrita</option>
                            </select>
                            <Toggle label="Cursiva" checked={!!style.headerItalic} onChange={v => patchStyle({ headerItalic: v })} />
                            <label style={styles.label}>Alineación</label>
                            <select value={style.headerAlign || 'auto'} onChange={e => patchStyle({ headerAlign: e.target.value })} style={inputStyle}>
                                <option value="auto">Automática</option>
                                <option value="left">Izquierda</option>
                                <option value="center">Centro</option>
                                <option value="right">Derecha</option>
                            </select>
                            <label style={styles.label}>Color de texto</label>
                            <input type="color" value={style.headerColor || '#ffffff'} onChange={e => patchStyle({ headerColor: e.target.value })} style={styles.colorInput} />
                            <label style={styles.label}>Color de fondo</label>
                            <input type="color" value={style.headerBackground || '#5E35B1'} onChange={e => patchStyle({ headerBackground: e.target.value })} style={styles.colorInput} />

                            <h4 style={styles.section}>Datos</h4>
                            <label style={styles.label}>Tamaño ({style.bodyFontSize || 13}px)</label>
                            <input type="range" min="10" max="20" value={style.bodyFontSize || 13} onChange={e => patchStyle({ bodyFontSize: Number(e.target.value) })} />
                            <label style={styles.label}>Peso</label>
                            <select value={style.bodyFontWeight || 400} onChange={e => patchStyle({ bodyFontWeight: Number(e.target.value) })} style={inputStyle}>
                                <option value={400}>Regular</option>
                                <option value={500}>Media</option>
                                <option value={600}>Seminegrita</option>
                            </select>
                            <label style={styles.label}>Alineación</label>
                            <select value={style.bodyAlign || 'auto'} onChange={e => patchStyle({ bodyAlign: e.target.value })} style={inputStyle}>
                                <option value="auto">Números a la derecha</option>
                                <option value="left">Izquierda</option>
                                <option value="center">Centro</option>
                                <option value="right">Derecha</option>
                            </select>
                            <label style={styles.label}>Color de texto</label>
                            <input type="color" value={style.bodyColor || '#202124'} onChange={e => patchStyle({ bodyColor: e.target.value })} style={styles.colorInput} />
                            <label style={styles.label}>Color de dimensiones</label>
                            <input type="color" value={style.dimTextColor || '#4a148c'} onChange={e => patchStyle({ dimTextColor: e.target.value })} style={styles.colorInput} />
                            <label style={styles.label}>Fondo de filas</label>
                            <input type="color" value={style.bodyBackground || '#ffffff'} onChange={e => patchStyle({ bodyBackground: e.target.value })} style={styles.colorInput} />
                            <label style={styles.label}>Fondo de filas alternas</label>
                            <input type="color" value={style.stripeBackground || '#f6f2fb'} onChange={e => patchStyle({ stripeBackground: e.target.value })} style={styles.colorInput} />

                            <h4 style={styles.section}>Fila de totales</h4>
                            <label style={styles.label}>Tamaño ({style.totalsFontSize || 13}px)</label>
                            <input type="range" min="10" max="22" value={style.totalsFontSize || 13} onChange={e => patchStyle({ totalsFontSize: Number(e.target.value) })} />
                            <label style={styles.label}>Peso</label>
                            <select value={style.totalsFontWeight || 700} onChange={e => patchStyle({ totalsFontWeight: Number(e.target.value) })} style={inputStyle}>
                                <option value={400}>Regular</option>
                                <option value={600}>Seminegrita</option>
                                <option value={700}>Negrita</option>
                                <option value={800}>Extra</option>
                            </select>
                            <Toggle label="Cursiva" checked={!!style.totalsItalic} onChange={v => patchStyle({ totalsItalic: v })} />
                            <label style={styles.label}>Alineación</label>
                            <select value={style.totalsAlign || 'auto'} onChange={e => patchStyle({ totalsAlign: e.target.value })} style={inputStyle}>
                                <option value="auto">Automática</option>
                                <option value="left">Izquierda</option>
                                <option value="center">Centro</option>
                                <option value="right">Derecha</option>
                            </select>
                            <label style={styles.label}>Color de texto</label>
                            <input type="color" value={style.totalsColor || '#4a148c'} onChange={e => patchStyle({ totalsColor: e.target.value })} style={styles.colorInput} />
                            <label style={styles.label}>Color de fondo</label>
                            <input type="color" value={style.totalsBackground || '#ede7f6'} onChange={e => patchStyle({ totalsBackground: e.target.value })} style={styles.colorInput} />
                        </>
                    )}

                    {isChartType(type) && (
                        <>
                            <Toggle label="Leyenda" checked={style.showLegend} onChange={v => patchStyle({ showLegend: v })} />
                            <label style={styles.label}>Posición de leyenda</label>
                            <select value={style.legendPosition || 'bottom'} onChange={e => patchStyle({ legendPosition: e.target.value })} style={inputStyle}>
                                <option value="bottom">Abajo</option>
                                <option value="top">Arriba</option>
                            </select>
                            {type !== 'pie' && (
                                <Toggle label="Cuadrícula" checked={style.showGrid} onChange={v => patchStyle({ showGrid: v })} />
                            )}
                            <Toggle label="Etiquetas de valor" checked={style.showLabels} onChange={v => patchStyle({ showLabels: v })} />
                            {style.showLabels && (
                                <>
                                    <label style={styles.label}>Formato de etiquetas</label>
                                    <select
                                        value={style.labelFormat || (type === 'pie' ? 'percent' : 'value')}
                                        onChange={e => patchStyle({ labelFormat: e.target.value })}
                                        style={inputStyle}
                                    >
                                        <option value="value">Valor</option>
                                        <option value="percent">Porcentaje del total</option>
                                        <option value="both">Valor y porcentaje</option>
                                    </select>
                                </>
                            )}
                            <label style={styles.label}>Top N (0 = todos)</label>
                            <input type="number" min="0" max="50" value={style.topN || 0} onChange={e => patchStyle({ topN: Number(e.target.value) || 0 })} style={inputStyle} />
                            {Number(style.topN) > 0 && dimensions.length === 1 && (
                                <Toggle
                                    label="Agrupar el resto en «Otros»"
                                    checked={style.topNOthers}
                                    onChange={v => patchStyle({ topNOthers: v })}
                                />
                            )}
                            <p style={styles.hint}>El Top N se calcula en el motor sobre el orden elegido (o la métrica de mayor a menor).</p>
                            <label style={styles.label}>Ordenar por</label>
                            <select value={style.sortKey || ''} onChange={e => patchStyle({ sortKey: e.target.value })} style={inputStyle}>
                                <option value="">Automático</option>
                                {metricShelf.map(name => <option key={name} value={name}>{getAlias(query, name)}</option>)}
                            </select>
                            <select value={style.sortDir || 'desc'} onChange={e => patchStyle({ sortDir: e.target.value })} style={inputStyle}>
                                <option value="desc">Mayor a menor</option>
                                <option value="asc">Menor a mayor</option>
                            </select>
                            {type !== 'pie' && (
                                <>
                                    <h4 style={styles.section}>Eje de categorías</h4>
                                    <Toggle
                                        label="Mostrar eje"
                                        checked={style.showCategoryAxis !== false}
                                        onChange={v => patchStyle({ showCategoryAxis: v })}
                                    />
                                    <Toggle
                                        label="Mostrar valores del eje"
                                        checked={style.showCategoryTicks !== false}
                                        onChange={v => patchStyle({ showCategoryTicks: v })}
                                    />
                                    <label style={styles.label}>Título</label>
                                    <input value={style.xAxisLabel || ''} onChange={e => patchStyle({ xAxisLabel: e.target.value })} style={inputStyle} />
                                    <label style={styles.label}>Rotación de etiquetas</label>
                                    <select
                                        value={String(style.categoryTickAngle || 0)}
                                        onChange={e => patchStyle({ categoryTickAngle: Number(e.target.value) })}
                                        style={inputStyle}
                                    >
                                        <option value="0">Horizontal</option>
                                        <option value="-35">Inclinada</option>
                                        <option value="-90">Vertical</option>
                                    </select>
                                    <label style={styles.label}>Tamaño de texto ({style.categoryTickSize || 12}px)</label>
                                    <input type="range" min="9" max="16" value={style.categoryTickSize || 12} onChange={e => patchStyle({ categoryTickSize: Number(e.target.value) })} />
                                    <label style={styles.label}>Recortar etiquetas (0 = no recortar)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="80"
                                        value={style.categoryTickMaxChars ?? 18}
                                        onChange={e => patchStyle({ categoryTickMaxChars: Math.max(0, Number(e.target.value) || 0) })}
                                        style={inputStyle}
                                    />
                                    <label style={styles.label}>Densidad de etiquetas</label>
                                    <select
                                        value={style.categoryTickInterval || 'auto'}
                                        onChange={e => patchStyle({ categoryTickInterval: e.target.value })}
                                        style={inputStyle}
                                    >
                                        <option value="auto">Automática</option>
                                        <option value="all">Todas</option>
                                        <option value="ends">Solo extremos</option>
                                    </select>

                                    <h4 style={styles.section}>Eje de valores</h4>
                                    <Toggle
                                        label="Mostrar eje"
                                        checked={style.showValueAxis !== false}
                                        onChange={v => patchStyle({ showValueAxis: v })}
                                    />
                                    <Toggle
                                        label="Mostrar valores del eje"
                                        checked={style.showValueTicks !== false}
                                        onChange={v => patchStyle({ showValueTicks: v })}
                                    />
                                    <label style={styles.label}>Título</label>
                                    <input value={style.yAxisLabel || ''} onChange={e => patchStyle({ yAxisLabel: e.target.value })} style={inputStyle} />
                                    <label style={styles.label}>Formato de valores</label>
                                    <select
                                        value={style.valueAxisFormat || 'inherit'}
                                        onChange={e => patchStyle({ valueAxisFormat: e.target.value })}
                                        style={inputStyle}
                                    >
                                        <option value="inherit">Igual que la métrica</option>
                                        {NUMBER_FORMATS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                    {style.valueAxisFormat && style.valueAxisFormat !== 'inherit' && (
                                        <>
                                            <label style={styles.label}>Decimales (vacío = automático)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="6"
                                                value={style.valueAxisDecimals ?? ''}
                                                onChange={e => patchStyle({
                                                    valueAxisDecimals: e.target.value === '' ? null : Math.max(0, Number(e.target.value) || 0)
                                                })}
                                                style={inputStyle}
                                            />
                                        </>
                                    )}
                                    <label style={styles.label}>Mínimo (vacío = automático)</label>
                                    <input
                                        value={style.valueAxisMin ?? ''}
                                        onChange={e => patchStyle({ valueAxisMin: e.target.value })}
                                        style={inputStyle}
                                        placeholder="auto"
                                    />
                                    <label style={styles.label}>Máximo (vacío = automático)</label>
                                    <input
                                        value={style.valueAxisMax ?? ''}
                                        onChange={e => patchStyle({ valueAxisMax: e.target.value })}
                                        style={inputStyle}
                                        placeholder="auto"
                                    />
                                    <label style={styles.label}>Tamaño de texto ({style.valueTickSize || 12}px)</label>
                                    <input type="range" min="9" max="16" value={style.valueTickSize || 12} onChange={e => patchStyle({ valueTickSize: Number(e.target.value) })} />
                                </>
                            )}
                            {metricShelf.length > 0 && (
                                <>
                                    <label style={styles.label}>Color por serie</label>
                                    {metricShelf.map((name, idx) => (
                                        <div key={name} style={styles.seriesRow}>
                                            <span style={styles.seriesName}>{getAlias(query, name)}</span>
                                            <input
                                                type="color"
                                                value={style.seriesColors?.[name] || (idx === 0 ? style.color : CHART_PALETTE[idx % CHART_PALETTE.length])}
                                                onChange={e => patchStyle({ seriesColors: { ...(style.seriesColors || {}), [name]: e.target.value } })}
                                                style={styles.colorInput}
                                            />
                                        </div>
                                    ))}
                                </>
                            )}
                        </>
                    )}

                    {type === 'map_mx' && (
                        <>
                            <h4 style={styles.section}>Mapa</h4>
                            <p style={styles.hint}>
                                Arrastra una dimensión con nombres de estado (columna "Dimensiones" arriba) y una
                                métrica numérica. El color va de claro (menor) a oscuro (mayor).
                            </p>
                            <Toggle label="Leyenda" checked={style.showLegend !== false} onChange={v => patchStyle({ showLegend: v })} />
                            <label style={styles.label}>Color sin datos</label>
                            <input
                                type="color"
                                value={style.noDataColor || '#e1e0d9'}
                                onChange={e => patchStyle({ noDataColor: e.target.value })}
                                style={styles.colorInput}
                            />

                            <h4 style={styles.section}>Degradado</h4>
                            <label style={styles.label}>Color mínimo</label>
                            <input
                                type="color"
                                value={style.mapColorMin || '#cde2fb'}
                                onChange={e => patchStyle({ mapColorMin: e.target.value })}
                                style={styles.colorInput}
                            />
                            <label style={styles.label}>Color máximo</label>
                            <input
                                type="color"
                                value={style.mapColorMax || '#0d366b'}
                                onChange={e => patchStyle({ mapColorMax: e.target.value })}
                                style={styles.colorInput}
                            />
                            {(style.mapColorMin || style.mapColorMax) && (
                                <button
                                    type="button"
                                    style={styles.linkBtn}
                                    onClick={() => patchStyle({ mapColorMin: undefined, mapColorMax: undefined })}
                                >
                                    Restablecer colores
                                </button>
                            )}

                            <h4 style={styles.section}>Etiquetas</h4>
                            <Toggle
                                label="Mostrar etiquetas"
                                checked={Boolean(style.showMapLabels)}
                                onChange={v => patchStyle({ showMapLabels: v })}
                            />
                            {style.showMapLabels && (
                                <>
                                    <label style={styles.label}>Contenido</label>
                                    <select
                                        value={style.mapLabelContent || 'name'}
                                        onChange={e => patchStyle({ mapLabelContent: e.target.value })}
                                        style={inputStyle}
                                    >
                                        <option value="name">Nombre</option>
                                        <option value="value">Valor</option>
                                        <option value="both">Nombre y valor</option>
                                    </select>
                                </>
                            )}

                            <h4 style={styles.section}>Zoom</h4>
                            <Toggle
                                label="Permitir zoom"
                                checked={style.enableZoom !== false}
                                onChange={v => patchStyle({ enableZoom: v })}
                            />
                            <p style={styles.hint}>
                                Botones +/−/restablecer sobre el mapa; arrastrar para desplazarse una vez acercado.
                            </p>
                        </>
                    )}

                    {(type === 'bar' || type === 'hbar' || type === 'combo' || type === 'area') && (
                        <>
                            <Toggle label="Apilado" checked={style.stacked} onChange={v => patchStyle({ stacked: v })} />
                            {(type === 'bar' || type === 'hbar' || type === 'combo') && (
                                <>
                                    <label style={styles.label}>Redondeo de barras ({style.barRadius})</label>
                                    <input type="range" min="0" max="16" value={style.barRadius} onChange={e => patchStyle({ barRadius: Number(e.target.value) })} />
                                </>
                            )}
                        </>
                    )}

                    {type === 'combo' && metricShelf.length > 0 && (
                        <>
                            <label style={styles.label}>Métricas en línea (el resto van en barras)</label>
                            {metricShelf.map(name => (
                                <Toggle
                                    key={name}
                                    label={getAlias(query, name)}
                                    checked={(style.comboLineMetrics || []).includes(name)}
                                    onChange={v => {
                                        const current = style.comboLineMetrics || [];
                                        patchStyle({
                                            comboLineMetrics: v ? [...current, name] : current.filter(item => item !== name)
                                        });
                                    }}
                                />
                            ))}
                        </>
                    )}

                    {(type === 'line' || type === 'area' || type === 'combo') && (
                        <>
                            <Toggle label="Mostrar puntos" checked={style.showDots} onChange={v => patchStyle({ showDots: v })} />
                            {type !== 'combo' && <Toggle label="Rellenar área" checked={style.fillArea} onChange={v => patchStyle({ fillArea: v })} />}
                            <label style={styles.label}>Grosor de línea ({style.strokeWidth})</label>
                            <input type="range" min="1" max="6" value={style.strokeWidth} onChange={e => patchStyle({ strokeWidth: Number(e.target.value) })} />
                        </>
                    )}

                    {type === 'pie' && (
                        <>
                            <label style={styles.label}>Hueco (donut) {style.innerRadius}%</label>
                            <input type="range" min="0" max="70" value={style.innerRadius} onChange={e => patchStyle({ innerRadius: Number(e.target.value) })} />
                        </>
                    )}

                    {type === 'kpi' && (
                        <>
                            <label style={styles.label}>Prefijo</label>
                            <input value={style.kpiPrefix} onChange={e => patchStyle({ kpiPrefix: e.target.value })} style={inputStyle} placeholder="$" />
                            <label style={styles.label}>Sufijo</label>
                            <input value={style.kpiSuffix} onChange={e => patchStyle({ kpiSuffix: e.target.value })} style={inputStyle} placeholder="%" />
                            <label style={styles.label}>Tamaño de cifra ({style.kpiFontSize}px)</label>
                            <input type="range" min="18" max="48" value={style.kpiFontSize} onChange={e => patchStyle({ kpiFontSize: Number(e.target.value) })} />

                        </>
                    )}

                    {type === 'text' && (
                        <>
                            <label style={styles.label}>Alineación horizontal</label>
                            <div style={styles.choiceRow}>
                                {[
                                    { value: 'left', label: 'Izq.' },
                                    { value: 'center', label: 'Centro' },
                                    { value: 'right', label: 'Der.' },
                                    { value: 'justify', label: 'Justificar' }
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => patchStyle({ align: opt.value })}
                                        style={{
                                            ...styles.choiceBtn,
                                            ...(style.align === opt.value ? styles.choiceBtnOn : {})
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <label style={styles.label}>Alineación vertical</label>
                            <div style={styles.choiceRow}>
                                {[
                                    { value: 'top', label: 'Arriba' },
                                    { value: 'middle', label: 'Centro' },
                                    { value: 'bottom', label: 'Abajo' }
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => patchStyle({ valign: opt.value })}
                                        style={{
                                            ...styles.choiceBtn,
                                            ...(style.valign === opt.value ? styles.choiceBtnOn : {})
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <label style={styles.label}>Fuente</label>
                            <select
                                value={style.fontFamily || 'inherit'}
                                onChange={e => patchStyle({ fontFamily: e.target.value })}
                                style={inputStyle}
                            >
                                {TEXT_FONT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <label style={styles.label}>Tamaño de texto ({style.fontSize || 16}px)</label>
                            <input type="range" min="10" max="72" value={style.fontSize || 16} onChange={e => patchStyle({ fontSize: Number(e.target.value) })} />
                            <label style={styles.label}>Peso</label>
                            <select
                                value={style.fontWeight || 400}
                                onChange={e => patchStyle({ fontWeight: Number(e.target.value) })}
                                style={inputStyle}
                            >
                                <option value={400}>Regular</option>
                                <option value={500}>Media</option>
                                <option value={700}>Negrita</option>
                                <option value={800}>Extra</option>
                            </select>
                            <Toggle label="Cursiva" checked={!!style.italic} onChange={v => patchStyle({ italic: v })} />
                            <Toggle label="Subrayado" checked={!!style.underline} onChange={v => patchStyle({ underline: v })} />
                            <label style={styles.label}>Mayúsculas</label>
                            <select
                                value={style.textTransform || 'none'}
                                onChange={e => patchStyle({ textTransform: e.target.value })}
                                style={inputStyle}
                            >
                                <option value="none">Como se escribe</option>
                                <option value="uppercase">MAYÚSCULAS</option>
                                <option value="capitalize">Tipo Título</option>
                                <option value="lowercase">minúsculas</option>
                            </select>
                            <label style={styles.label}>Interlineado ({Number(style.lineHeight || 1.45).toFixed(2)})</label>
                            <input
                                type="range"
                                min="100"
                                max="220"
                                value={Math.round((style.lineHeight || 1.45) * 100)}
                                onChange={e => patchStyle({ lineHeight: Number(e.target.value) / 100 })}
                            />
                            <label style={styles.label}>Espaciado de letras ({style.letterSpacing || 0}px)</label>
                            <input
                                type="range"
                                min="-1"
                                max="8"
                                step="0.5"
                                value={style.letterSpacing || 0}
                                onChange={e => patchStyle({ letterSpacing: Number(e.target.value) })}
                            />
                            <label style={styles.label}>Relleno horizontal ({style.textPaddingX ?? style.textPadding ?? 8}px)</label>
                            <input
                                type="range"
                                min="0"
                                max="64"
                                value={style.textPaddingX ?? style.textPadding ?? 8}
                                onChange={e => patchStyle({ textPaddingX: Number(e.target.value) })}
                            />
                            <label style={styles.label}>Relleno vertical ({style.textPaddingY ?? style.textPadding ?? 8}px)</label>
                            <input
                                type="range"
                                min="0"
                                max="64"
                                value={style.textPaddingY ?? style.textPadding ?? 8}
                                onChange={e => patchStyle({ textPaddingY: Number(e.target.value) })}
                            />
                            <label style={styles.label}>Borde ({style.textBorderWidth || 0}px)</label>
                            <input
                                type="range"
                                min="0"
                                max="8"
                                value={style.textBorderWidth || 0}
                                onChange={e => patchStyle({ textBorderWidth: Number(e.target.value) })}
                            />
                            {(style.textBorderWidth || 0) > 0 && (
                                <>
                                    <label style={styles.label}>Color del borde</label>
                                    <input
                                        type="color"
                                        value={style.textBorderColor || '#dadce0'}
                                        onChange={e => patchStyle({ textBorderColor: e.target.value })}
                                        style={styles.colorInput}
                                    />
                                </>
                            )}
                        </>
                    )}

                    {type === 'divider' && (
                        <>
                            <label style={styles.label}>Orientación</label>
                            <select value={style.orientation || 'horizontal'} onChange={e => patchStyle({ orientation: e.target.value })} style={inputStyle}>
                                <option value="horizontal">Horizontal</option>
                                <option value="vertical">Vertical</option>
                            </select>
                            <label style={styles.label}>Grosor ({style.thickness || 2}px)</label>
                            <input type="range" min="1" max="12" value={style.thickness || 2} onChange={e => patchStyle({ thickness: Number(e.target.value) })} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const FieldPill = ({
    name, alias, tone, agg, aggSpec, innerFields = [], isRatio, highlight, format, decimals, formula,
    totalAgg, totalFormula, formulaSuggestions = [], knownNames = [], refMetrics = {}, onTotalAgg, onTotalFormula, onFormulaChange, onRefAgg,
    conditionalFormats = [], onConditionalFormats,
    onAgg, onAggSpec, onFormat, onDecimals, onAlias, onRemove, onDragStart, onDragOver, onDragLeave, onDrop
}) => {
    const spec = aggSpec || parseMetricSpec(agg);
    const showNested = tone === 'met' && onFormat && (isRatio || onAggSpec);
    const [open, setOpen] = useState(false);

    const formatFields = onFormat ? (
        <>
            <label style={{ ...styles.metricField, gridColumn: '1 / -1' }}>
                <span style={styles.metricFieldLabel}>Formato</span>
                <select
                    value={format || 'inherit'}
                    onChange={e => onFormat(e.target.value)}
                    style={styles.metricSelect}
                >
                    <option value="inherit">Por defecto del widget</option>
                    {NUMBER_FORMATS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </label>
            <label style={{ ...styles.metricField, gridColumn: '1 / -1' }}>
                <span style={styles.metricFieldLabel}>Decimales</span>
                <input
                    type="number"
                    min="0"
                    max="8"
                    placeholder="Auto"
                    value={decimals === 0 || decimals ? decimals : ''}
                    onChange={e => onDecimals && onDecimals(e.target.value)}
                    style={styles.metricSelect}
                />
            </label>
            <label style={{ ...styles.metricField, gridColumn: '1 / -1' }}>
                <span style={styles.metricFieldLabel}>Fila de totales</span>
                <select
                    value={totalAgg || 'auto'}
                    onChange={e => onTotalAgg && onTotalAgg(e.target.value)}
                    style={styles.metricSelect}
                >
                    {TOTAL_AGG_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </label>
            {totalAgg === 'formula' && (
                <>
                    <label style={{ ...styles.metricField, gridColumn: '1 / -1' }}>
                        <span style={styles.metricFieldLabel}>Fórmula del total</span>
                        <FormulaInput
                            multiline={false}
                            value={totalFormula || ''}
                            placeholder={formula || 'Impresiones / O_Impresiones'}
                            suggestions={formulaSuggestions}
                            includeFunctions={false}
                            onChange={next => onTotalFormula && onTotalFormula(next)}
                            style={styles.metricSelect}
                        />
                    </label>
                    <div style={styles.metricNote}>Usa los totales de las otras columnas. Ejemplo: Impresiones / O_Impresiones (4.6M ÷ 12.3M), no la suma de los % de cada fila.</div>
                </>
            )}
            {onConditionalFormats && (
                <div style={styles.cfBox}>
                    <div style={styles.metricFieldLabel}>Formato condicional</div>
                    <p style={styles.metricNote}>La primera regla que cumpla pinta la celda. Los % se comparan como decimal (0.8 = 80%).</p>
                    {(conditionalFormats || []).map((rule, index) => (
                        <div key={rule.id || index} style={styles.cfRow}>
                            <select
                                value={rule.op || 'gt'}
                                onChange={e => onConditionalFormats(conditionalFormats.map((item, i) => (
                                    i === index ? { ...item, op: e.target.value } : item
                                )))}
                                style={{ ...styles.metricSelect, flex: '1 1 110px', width: 'auto' }}
                            >
                                {CONDITION_OPS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            {rule.op !== 'empty' && rule.op !== 'not_empty' && (
                                <input
                                    type="number"
                                    value={rule.value ?? ''}
                                    onChange={e => onConditionalFormats(conditionalFormats.map((item, i) => (
                                        i === index ? { ...item, value: e.target.value === '' ? '' : Number(e.target.value) } : item
                                    )))}
                                    style={{ ...styles.metricSelect, width: '72px', flex: '0 0 72px' }}
                                />
                            )}
                            {rule.op === 'between' && (
                                <input
                                    type="number"
                                    value={rule.valueTo ?? ''}
                                    onChange={e => onConditionalFormats(conditionalFormats.map((item, i) => (
                                        i === index ? { ...item, valueTo: e.target.value === '' ? '' : Number(e.target.value) } : item
                                    )))}
                                    style={{ ...styles.metricSelect, width: '72px', flex: '0 0 72px' }}
                                />
                            )}
                            <label style={styles.cfColor} title="Color de texto">
                                <input
                                    type="color"
                                    value={rule.color || '#137333'}
                                    onChange={e => onConditionalFormats(conditionalFormats.map((item, i) => (
                                        i === index ? { ...item, color: e.target.value } : item
                                    )))}
                                    style={styles.cfColorInput}
                                />
                            </label>
                            <label style={styles.cfColor} title="Color de fondo">
                                <input
                                    type="color"
                                    value={rule.background || '#e6f4ea'}
                                    onChange={e => onConditionalFormats(conditionalFormats.map((item, i) => (
                                        i === index ? { ...item, background: e.target.value } : item
                                    )))}
                                    style={styles.cfColorInput}
                                />
                            </label>
                            <label style={styles.cfBold}>
                                <input
                                    type="checkbox"
                                    checked={!!rule.bold}
                                    onChange={e => onConditionalFormats(conditionalFormats.map((item, i) => (
                                        i === index ? { ...item, bold: e.target.checked } : item
                                    )))}
                                />
                                N
                            </label>
                            <button
                                type="button"
                                style={styles.pillX}
                                onClick={() => onConditionalFormats(conditionalFormats.filter((_, i) => i !== index))}
                            >×</button>
                        </div>
                    ))}
                    <button
                        type="button"
                        style={styles.cfAdd}
                        onClick={() => onConditionalFormats([...(conditionalFormats || []), createConditionalRule()])}
                    >
                        + Añadir regla
                    </button>
                </div>
            )}
        </>
    ) : null;

    if (showNested) {
        return (
            <div
                style={{
                    ...styles.metricCard,
                    padding: open ? '8px' : '4px 6px',
                    gap: open ? '8px' : 0,
                    outline: highlight ? '2px solid #137333' : '1px solid #c8e6c9'
                }}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
            >
                <div style={styles.metricHeader}>
                    <span
                        draggable
                        onDragStart={onDragStart}
                        style={styles.grip}
                        title="Arrastra arriba o abajo para reordenar"
                    >⋮⋮</span>
                    <button
                        type="button"
                        style={styles.metricToggle}
                        aria-expanded={open}
                        title={open ? 'Contraer opciones' : 'Expandir opciones'}
                        onClick={() => setOpen(v => !v)}
                    >
                        {open ? '▾' : '▸'}
                    </button>
                    <span style={{ ...styles.pillFields, flex: 1, minWidth: 0 }}>
                        {open ? (
                            <>
                                <textarea
                                    value={alias === name ? '' : alias}
                                    placeholder={`${name} (Enter = salto de línea)`}
                                    onChange={e => onAlias(e.target.value)}
                                    onKeyDown={e => e.stopPropagation()}
                                    rows={2}
                                    style={{ ...styles.aliasInput, width: '100%', resize: 'vertical', minHeight: '36px' }}
                                    title={`Nombre original: ${name}. Enter para salto de línea en el encabezado.`}
                                />
                                {alias && alias !== name && <span style={styles.origName}>{name}</span>}
                            </>
                        ) : (
                            <input
                                value={alias === name ? '' : alias}
                                placeholder={name}
                                onChange={e => onAlias(e.target.value)}
                                onKeyDown={e => e.stopPropagation()}
                                style={{ ...styles.aliasInput, width: '100%' }}
                                title={`Nombre original: ${name}. Pulsa ▸ para formato y agregación.`}
                            />
                        )}
                    </span>
                    <button type="button" style={styles.pillX} onClick={onRemove}>×</button>
                </div>
                {open && (
                <div style={styles.metricGrid}>
                    <button
                        type="button"
                        style={styles.metricSummary}
                        onClick={() => setOpen(false)}
                        title="Contraer opciones"
                    >
                        {isRatio ? (formula || 'Campo calculado') : describeMetricSpec(spec)}
                    </button>
                    {!isRatio && onAggSpec && (
                    <>
                    <label style={styles.metricField}>
                        <span style={styles.metricFieldLabel}>Resultado</span>
                        <select
                            value={spec.agg || 'sum'}
                            onChange={e => onAggSpec({ agg: e.target.value })}
                            style={styles.metricSelect}
                        >
                            {AGG_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </label>
                    <label style={styles.metricField}>
                        <span style={styles.metricFieldLabel}>De</span>
                        <select
                            value={spec.innerAgg || ''}
                            onChange={e => onAggSpec({
                                innerAgg: e.target.value,
                                innerBy: e.target.value ? (spec.innerBy || innerFields[0] || '') : ''
                            })}
                            style={styles.metricSelect}
                        >
                            {INNER_AGG_OPTIONS.map(opt => (
                                <option key={opt.value || 'values'} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </label>
                    {spec.innerAgg ? (
                        <label style={{ ...styles.metricField, gridColumn: '1 / -1' }}>
                            <span style={styles.metricFieldLabel}>Primero agrupa por</span>
                            <select
                                value={spec.innerBy || ''}
                                onChange={e => onAggSpec({ innerBy: e.target.value })}
                                style={styles.metricSelect}
                            >
                                <option value="">Elegir campo…</option>
                                {innerFields.map(field => (
                                    <option key={field} value={field}>{field}</option>
                                ))}
                            </select>
                        </label>
                    ) : (
                        <div style={styles.metricNote}>Usa «De» para sumar máximos, promediar totales, etc.</div>
                    )}
                    </>
                    )}
                    {isRatio && (
                        <>
                            <div style={styles.metricNote}>Campo calculado. Ajusta cómo se agregan los campos de la fórmula.</div>
                            {extractAggCalls(formula).map((call, index) => (
                                <label key={`${call.column}-${index}`} style={{ ...styles.metricField, gridColumn: '1 / -1' }}>
                                    <span style={styles.metricFieldLabel}>{call.column}</span>
                                    <select
                                        value={call.agg}
                                        onChange={e => {
                                            const fn = AGG_FN_FOR[e.target.value] || 'SUM';
                                            onFormulaChange && onFormulaChange(replaceNthAggFn(formula, index, fn));
                                        }}
                                        style={styles.metricSelect}
                                    >
                                        {AGG_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </label>
                            ))}
                            {extractAggCalls(formula).length === 0 && extractFormulaRefs(formula, knownNames).filter(ref => ref !== name).map(ref => (
                                <label key={ref} style={{ ...styles.metricField, gridColumn: '1 / -1' }}>
                                    <span style={styles.metricFieldLabel}>{ref}</span>
                                    <select
                                        value={parseMetricSpec(refMetrics[ref]).agg || 'sum'}
                                        onChange={e => onRefAgg && onRefAgg(ref, e.target.value)}
                                        style={styles.metricSelect}
                                    >
                                        {AGG_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </label>
                            ))}
                        </>
                    )}
                    {formatFields}
                </div>
                )}
            </div>
        );
    }

    return (
    <span
        style={{
            ...styles.pill,
            ...(tone === 'dim' ? styles.pillDim : styles.pillMet),
            outline: highlight ? '2px solid #5E35B1' : 'none'
        }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
    >
        <span
            draggable
            onDragStart={onDragStart}
            style={styles.grip}
            title="Arrastra arriba o abajo para reordenar"
        >⋮⋮</span>
        <span style={styles.pillFields}>
            <input
                value={alias === name ? '' : alias}
                placeholder={name}
                onChange={e => onAlias(e.target.value)}
                onKeyDown={e => e.stopPropagation()}
                style={{ ...styles.aliasInput, width: '100%', minWidth: 0 }}
                title={`Nombre original: ${name}`}
            />
        </span>
        {agg && (
            <select
                value={typeof agg === 'string' ? agg : (spec.agg || 'sum')}
                onChange={e => (onAggSpec ? onAggSpec({ agg: e.target.value }) : onAgg(e.target.value))}
                style={styles.aggSelect}
                title="Agregación"
            >
                {AGG_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        )}
        {isRatio && <em style={styles.ratioTag}>ratio</em>}
        <button type="button" style={styles.pillX} onClick={onRemove}>×</button>
    </span>
    );
};

const Toggle = ({ label, checked, onChange }) => (
    <label style={styles.toggle}>
        <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
        {label}
    </label>
);

const styles = {
    panel: { display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0, overflowX: 'hidden' },
    titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' },
    title: { margin: 0, fontSize: '1rem', color: '#5E35B1' },
    hideBtn: {
        border: '1px solid #e0d4f7',
        background: '#faf7ff',
        color: '#5E35B1',
        borderRadius: '6px',
        width: '26px',
        height: '26px',
        cursor: 'pointer',
        fontSize: '1rem',
        lineHeight: 1
    },
    empty: { color: '#5f6368', fontSize: '0.9rem', lineHeight: 1.5, padding: '12px 4px' },
    hint: { margin: 0, fontSize: '0.72rem', color: '#5f6368' },
    tabs: { display: 'flex', gap: '6px' },
    tab: { flex: 1, padding: '6px', border: '1px solid #dadce0', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' },
    tabOn: { flex: 1, padding: '6px', border: '1px solid #5E35B1', background: '#f3e5f5', color: '#4a148c', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 },
    label: { fontSize: '0.8rem', fontWeight: 600, color: '#5f6368', marginTop: '6px' },
    grainRow: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 },
    grainLabel: { fontSize: '0.76rem', color: '#5f6368' },
    section: { margin: '10px 0 0', fontSize: '0.85rem', color: '#5E35B1' },
    shelf: {
        minHeight: '40px',
        borderRadius: '8px',
        padding: '6px',
        background: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap',
        gap: '4px',
        alignItems: 'stretch'
    },
    shelfHint: { fontSize: '0.78rem', color: '#9aa0a6' },
    pill: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        borderRadius: '10px',
        padding: '4px 8px',
        fontSize: '0.75rem',
        fontWeight: 600,
        width: '100%',
        boxSizing: 'border-box'
    },
    grip: {
        cursor: 'grab',
        letterSpacing: '-2px',
        opacity: 0.65,
        userSelect: 'none',
        padding: '0 2px',
        fontSize: '0.85rem'
    },
    pillDim: { background: '#e8f0fe', color: '#174ea6' },
    pillMet: { background: '#e6f4ea', color: '#137333' },
    metricCard: {
        width: '100%',
        boxSizing: 'border-box',
        background: '#f1f8f2',
        borderRadius: '8px',
        padding: '4px 6px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        color: '#137333'
    },
    metricHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        minHeight: '26px'
    },
    metricToggle: {
        border: 'none',
        background: '#fff',
        color: '#137333',
        width: '22px',
        height: '22px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '0.75rem',
        lineHeight: 1,
        padding: 0,
        flexShrink: 0
    },
    metricSummary: {
        gridColumn: '1 / -1',
        fontSize: '0.72rem',
        fontWeight: 700,
        color: '#0d652d',
        background: '#fff',
        border: 'none',
        borderRadius: '6px',
        padding: '5px 8px',
        lineHeight: 1.3,
        textAlign: 'left',
        cursor: 'pointer',
        width: '100%'
    },
    metricGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '8px',
        marginTop: '8px'
    },
    metricField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        minWidth: 0
    },
    metricFieldLabel: {
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        color: '#2e7d32'
    },
    metricSelect: {
        width: '100%',
        boxSizing: 'border-box',
        border: '1px solid #a5d6a7',
        background: '#fff',
        borderRadius: '6px',
        padding: '5px 6px',
        fontSize: '0.75rem',
        color: '#137333',
        fontWeight: 600
    },
    metricNote: {
        gridColumn: '1 / -1',
        fontSize: '0.68rem',
        color: '#3c7a44',
        lineHeight: 1.35
    },
    pillFields: { display: 'flex', flexDirection: 'column', minWidth: '90px' },
    aliasInput: {
        border: 'none',
        background: 'transparent',
        color: 'inherit',
        fontWeight: 700,
        fontSize: '0.75rem',
        width: '110px',
        outline: 'none',
        fontFamily: 'inherit',
        lineHeight: 1.25
    },
    cfBox: { gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '6px' },
    cfRow: { display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' },
    cfColor: { display: 'flex', width: '22px', height: '22px', overflow: 'hidden', borderRadius: '4px', border: '1px solid #a5d6a7', flexShrink: 0 },
    cfColorInput: { width: '28px', height: '28px', border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' },
    cfBold: { fontSize: '0.72rem', fontWeight: 800, color: '#137333', display: 'flex', alignItems: 'center', gap: '2px' },
    cfAdd: {
        alignSelf: 'flex-start',
        border: '1px dashed #a5d6a7',
        background: '#fff',
        color: '#137333',
        borderRadius: '6px',
        padding: '4px 8px',
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700
    },
    origName: { fontSize: '0.62rem', fontWeight: 500, opacity: 0.75 },
    pillX: { border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', fontWeight: 'bold' },
    aggSelect: { border: 'none', background: 'transparent', fontSize: '0.7rem', color: 'inherit', maxWidth: '78px' },
    ratioTag: { fontSize: '0.65rem', fontStyle: 'normal', opacity: 0.8 },
    styleGrid: { display: 'flex', flexDirection: 'column', gap: '6px' },
    choiceRow: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
    choiceBtn: {
        flex: 1,
        minWidth: '56px',
        padding: '5px 6px',
        border: '1px solid #dadce0',
        background: '#fff',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '0.72rem',
        color: '#3c4043'
    },
    choiceBtnOn: {
        border: '1px solid #5E35B1',
        background: '#f3e5f5',
        color: '#4a148c',
        fontWeight: 700
    },
    swatches: { display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' },
    swatch: { width: '22px', height: '22px', borderRadius: '50%', border: 'none', cursor: 'pointer' },
    colorInput: { width: '28px', height: '28px', border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' },
    linkBtn: {
        appearance: 'none', border: 'none', background: 'transparent', padding: 0, marginTop: '4px',
        fontSize: '0.76rem', fontWeight: 600, color: '#5E35B1', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit'
    },
    toggle: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#202124' },
    seriesRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' },
    seriesName: { fontSize: '0.78rem', color: '#202124', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
    spanRow: { display: 'flex', gap: '8px' },
    spanBox: { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.75rem', color: '#5f6368', fontWeight: 600, flex: 1 },
    spanInput: { padding: '6px 8px', borderRadius: '6px', border: '1px solid #dadce0' },
};

export default WidgetConfigurator;
